import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useFloatingPosition } from '$/hooks/useFloatingPosition';
import IconFont from '$/components/IconFont';
import styles from './index.module.scss';

// ─── Types ───────────────────────────────────────────────────────────────────

type Align = 'start' | 'center' | 'end';
type Side = 'top' | 'right' | 'bottom' | 'left';

interface DropdownMenuProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
  showChevron?: boolean;
}

interface DropdownMenuContentProps {
  children: React.ReactNode;
  className?: string;
  width?: React.CSSProperties['width'];
  side?: Side;
  align?: Align;
  sideOffset?: number;
  alignOffset?: number;
  avoidCollisions?: boolean;
  collisionPadding?: number;
}

interface DropdownMenuItemProps<TValue = unknown> {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  selected?: boolean;
  value?: TValue;
  onSelect?: (value: TValue) => void;
}

interface DropdownMenuCheckboxItemProps {
  children: React.ReactNode;
  className?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
}

interface DropdownMenuRadioGroupProps<TValue = unknown> {
  children: React.ReactNode;
  value?: TValue;
  onValueChange?: (value: TValue) => void;
}

interface DropdownMenuRadioItemProps<TValue = unknown> {
  children: React.ReactNode;
  className?: string;
  value: TValue;
  disabled?: boolean;
}

interface DropdownMenuSubProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface DropdownMenuSubTriggerProps {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

interface DropdownMenuSubContentProps {
  children: React.ReactNode;
  className?: string;
  sideOffset?: number;
  alignOffset?: number;
  avoidCollisions?: boolean;
  collisionPadding?: number;
}

interface DropdownMenuLabelProps {
  children: React.ReactNode;
  className?: string;
  inset?: boolean;
}

interface DropdownMenuSeparatorProps {
  className?: string;
}

interface DropdownMenuGroupProps {
  children: React.ReactNode;
  className?: string;
}

// ─── Contexts ────────────────────────────────────────────────────────────────

interface MenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  /**
   * 本菜单树内所有已挂载的浮层节点（根内容 + 各级子菜单内容）。
   * 子菜单经 portal 渲染到 body，不在根内容 DOM 内，需登记进来，
   * 否则点击子菜单会被判为“点击外部”而误关整棵菜单。
   */
  portalsRef: React.MutableRefObject<Set<HTMLElement>>;
}

interface RadioGroupContextValue {
  value?: unknown;
  onValueChange?: (value: unknown) => void;
}

interface SubMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  /** 取消挂起的关闭计时器；触发器与内容区共享同一计时器，避免跨间隙误关。 */
  cancelClose: () => void;
  /** 延时关闭子菜单，进入触发器或内容区时应先 cancelClose。 */
  scheduleClose: () => void;
}

const MenuContext = createContext<MenuContextValue>({
  open: false,
  setOpen: () => {},
  triggerRef: { current: null },
  portalsRef: { current: new Set() },
});

const RadioGroupContext = createContext<RadioGroupContextValue>({});

const SubMenuContext = createContext<SubMenuContextValue>({
  open: false,
  setOpen: () => {},
  triggerRef: { current: null },
  cancelClose: () => {},
  scheduleClose: () => {},
});

// ─── Components ──────────────────────────────────────────────────────────────

export function DropdownMenu({
  children, open: controlledOpen, defaultOpen = false, onOpenChange,
}: DropdownMenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const triggerRef = useRef<HTMLElement>(null);
  const portalsRef = useRef<Set<HTMLElement>>(new Set());

  const setOpen = useCallback((value: boolean) => {
    if (!isControlled) setUncontrolledOpen(value);
    onOpenChange?.(value);
  }, [isControlled, onOpenChange]);

  const ctx = useMemo(() => ({
    open, setOpen, triggerRef, portalsRef,
  }), [open, setOpen]);

  return (
    <MenuContext.Provider value={ctx}>
      {children}
    </MenuContext.Provider>
  );
}

/**
 * 下拉触发器统一承接展开态与可选箭头，避免业务组件各自处理方向。
 */
export function DropdownMenuTrigger({
  children, asChild, className, showChevron,
}: DropdownMenuTriggerProps) {
  const { open, setOpen, triggerRef } = useContext(MenuContext);
  const handledByPointerRef = useRef(false);
  const disabled = asChild && React.isValidElement(children)
    ? Boolean((children as React.ReactElement<any>).props.disabled)
    : false;

  // 触发器在打开后变为禁用时立即收起，避免导出等锁定状态仍残留下拉层。
  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open, setOpen]);

  /**
   * 主键 pointerdown 立即切换菜单，规避 Safari 触控板轻触时 click 不触发的问题。
   */
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (disabled || e.button !== 0) return;
    e.stopPropagation();
    handledByPointerRef.current = true;
    setOpen(!open);
  }, [disabled, open, setOpen]);

  /**
   * 键盘或无 pointer 事件场景仍依赖 click 切换菜单，并跳过 pointerdown 已处理的重复切换。
   */
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (handledByPointerRef.current) {
      handledByPointerRef.current = false;
      return;
    }
    setOpen(!open);
  }, [disabled, open, setOpen]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
  }, [disabled, setOpen]);

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<any>;
    return React.cloneElement(child, {
      ref: triggerRef,
      onPointerDown: (e: React.PointerEvent) => {
        handlePointerDown(e);
        child.props.onPointerDown?.(e);
      },
      onClick: (e: React.MouseEvent) => {
        handleClick(e);
        child.props.onClick?.(e);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        handleKeyDown(e);
        child.props.onKeyDown?.(e);
      },
      'data-state': open ? 'open' : 'closed',
      'aria-expanded': open,
      'aria-haspopup': 'menu',
    });
  }

  return (
    <button
      ref={triggerRef as React.RefObject<HTMLButtonElement>}
      className={classNames(styles['dropdown-trigger'], open && styles['dropdown-trigger--open'], className)}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-state={open ? 'open' : 'closed'}
      aria-expanded={open}
      aria-haspopup="menu"
      type="button"
    >
      {children}
      {showChevron && (
        <IconFont
          name="chevronDownFill"
          fontSize={8}
          className={styles['dropdown-trigger-chevron']}
        />
      )}
    </button>
  );
}

/**
 * 菜单内容通过 portal 渲染，并在捕获阶段处理外部点击关闭。
 */
export function DropdownMenuContent({
  children,
  className,
  width,
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  alignOffset = 0,
  avoidCollisions = true,
  collisionPadding = 8,
}: DropdownMenuContentProps) {
  const {
    open, setOpen, triggerRef, portalsRef,
  } = useContext(MenuContext);

  const { contentRef, floatingStyle } = useFloatingPosition(open, triggerRef, {
    side: side === 'left' || side === 'right' ? side : side,
    sideOffset,
    align,
    alignOffset,
    avoidCollisions,
    collisionPadding,
  });

  // 打开时把根内容浮层登记进菜单树，供“点击外部”判定时排除。
  useEffect(() => {
    if (!open) return undefined;
    const node = contentRef.current;
    if (!node) return undefined;
    const portals = portalsRef.current;
    portals.add(node);
    return () => {
      portals.delete(node);
    };
  }, [open, portalsRef, contentRef]);

  // 延迟挂载外部 pointerdown 监听，避免与触发器 pointerdown 打开菜单的同一次手势冲突。
  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDownOutside = (e: PointerEvent) => {
      const target = e.target as Node;
      const insideMenuTree = Array.from(portalsRef.current).some((node) => node.contains(target));
      if (insideMenuTree || triggerRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const timerId = window.setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDownOutside, true);
    }, 0);
    document.addEventListener('keydown', handleEscape);
    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener('pointerdown', handlePointerDownOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, setOpen, triggerRef, portalsRef]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      ref={contentRef}
      className={classNames(styles['dropdown-content'], className)}
      role="menu"
      tabIndex={-1}
      style={{
        ...floatingStyle,
        width,
        minWidth: width ?? 180,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

export function DropdownMenuGroup({ children, className }: DropdownMenuGroupProps) {
  return (
    <div className={classNames(styles['dropdown-group'], className)} role="group">
      {children}
    </div>
  );
}

export function DropdownMenuLabel({ children, className, inset }: DropdownMenuLabelProps) {
  return (
    <div
      className={classNames(styles['dropdown-label'], inset && styles['dropdown-label--inset'], className)}
      role="presentation"
    >
      {children}
    </div>
  );
}

export function DropdownMenuItem<TValue = unknown>({
  children, className, disabled, selected, value, onSelect,
}: DropdownMenuItemProps<TValue>) {
  const { setOpen } = useContext(MenuContext);

  const handleClick = useCallback(() => {
    if (disabled) return;
    onSelect?.(value as TValue);
    setOpen(false);
  }, [disabled, onSelect, setOpen, value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <div
      className={classNames(
        styles['dropdown-item'],
        selected && styles['dropdown-item--selected'],
        disabled && styles['dropdown-item--disabled'],
        className,
      )}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled}
    >
      {children}
    </div>
  );
}

export function DropdownMenuCheckboxItem({
  children,
  className,
  checked,
  onCheckedChange,
  disabled,
}: DropdownMenuCheckboxItemProps) {
  const handleClick = useCallback(() => {
    if (disabled) return;
    onCheckedChange?.(!checked);
  }, [disabled, checked, onCheckedChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <div
      className={classNames(
        styles['dropdown-item'],
        styles['dropdown-checkbox-item'],
        disabled && styles['dropdown-item--disabled'],
        className,
      )}
      role="menuitemcheckbox"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <span className={styles['dropdown-item-indicator']}>
        {checked && (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 7L6 10L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {children}
    </div>
  );
}

export function DropdownMenuRadioGroup<TValue = unknown>({
  children,
  value,
  onValueChange,
}: DropdownMenuRadioGroupProps<TValue>) {
  const ctx = useMemo<RadioGroupContextValue>(() => ({
    value,
    onValueChange: onValueChange as ((value: unknown) => void) | undefined,
  }), [value, onValueChange]);
  return (
    <RadioGroupContext.Provider value={ctx}>
      <div role="group">{children}</div>
    </RadioGroupContext.Provider>
  );
}

export function DropdownMenuRadioItem<TValue = unknown>({
  children, className, value, disabled,
}: DropdownMenuRadioItemProps<TValue>) {
  const { value: groupValue, onValueChange } = useContext(RadioGroupContext);
  const checked = groupValue === value;

  const handleClick = useCallback(() => {
    if (disabled) return;
    onValueChange?.(value);
  }, [disabled, value, onValueChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <div
      className={classNames(
        styles['dropdown-item'],
        styles['dropdown-radio-item'],
        disabled && styles['dropdown-item--disabled'],
        className,
      )}
      role="menuitemradio"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <span className={styles['dropdown-item-indicator']}>
        {checked && (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="3" fill="currentColor" />
          </svg>
        )}
      </span>
      {children}
    </div>
  );
}

export function DropdownMenuSeparator({ className }: DropdownMenuSeparatorProps) {
  return <div className={classNames(styles['dropdown-separator'], className)} role="separator" />;
}

// ─── Sub Menu ────────────────────────────────────────────────────────────────

/**
 * 子菜单相对触发行右缘的偏移。触发行位于父菜单 8px 内边距之内，
 * 故需 8px 抵消父菜单内边距 + 2px 视觉间隙，避免弹出内容与父菜单重叠。
 * 与父菜单 .dropdown-content 的 padding 保持一致。
 */
const SUB_CONTENT_SIDE_OFFSET = 10;

export function DropdownMenuSub({
  children, open: controlledOpen, defaultOpen = false, onOpenChange,
}: DropdownMenuSubProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const triggerRef = useRef<HTMLElement>(null);

  const setOpen = useCallback((value: boolean) => {
    if (!isControlled) setUncontrolledOpen(value);
    onOpenChange?.(value);
  }, [isControlled, onOpenChange]);

  // 触发器与内容区共享同一关闭计时器，跨越两者之间的间隙时不会误关子菜单。
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  }, []);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  }, [cancelClose, setOpen]);
  useEffect(() => cancelClose, [cancelClose]);

  const ctx = useMemo(
    () => ({
      open, setOpen, triggerRef, cancelClose, scheduleClose,
    }),
    [open, setOpen, cancelClose, scheduleClose],
  );

  return (
    <SubMenuContext.Provider value={ctx}>
      {children}
    </SubMenuContext.Provider>
  );
}

export function DropdownMenuSubTrigger({ children, className, disabled }: DropdownMenuSubTriggerProps) {
  const {
    open, setOpen, triggerRef, cancelClose, scheduleClose,
  } = useContext(SubMenuContext);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;
    cancelClose();
    setOpen(true);
  }, [disabled, cancelClose, setOpen]);

  const handleMouseLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  return (
    <div
      ref={triggerRef as React.RefObject<HTMLDivElement>}
      className={classNames(
        styles['dropdown-item'],
        styles['dropdown-sub-trigger'],
        open && styles['dropdown-sub-trigger--open'],
        disabled && styles['dropdown-item--disabled'],
        className,
      )}
      role="menuitem"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => !disabled && setOpen(!open)}
    >
      {children}
      <svg className={styles['dropdown-sub-chevron']} width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export function DropdownMenuSubContent({
  children, className, sideOffset = SUB_CONTENT_SIDE_OFFSET, alignOffset = 0,
  avoidCollisions = true, collisionPadding = 8,
}: DropdownMenuSubContentProps) {
  const {
    open, triggerRef, cancelClose, scheduleClose,
  } = useContext(SubMenuContext);
  const { portalsRef } = useContext(MenuContext);

  const { contentRef, floatingStyle } = useFloatingPosition(open, triggerRef, {
    side: 'right',
    sideOffset,
    align: 'start',
    alignOffset,
    avoidCollisions,
    collisionPadding,
  });

  // 子菜单浮层同样登记进根菜单树，点击子菜单内容（含群众阵列表单）不再误关整棵菜单。
  useEffect(() => {
    if (!open) return undefined;
    const node = contentRef.current;
    if (!node) return undefined;
    const portals = portalsRef.current;
    portals.add(node);
    return () => {
      portals.delete(node);
    };
  }, [open, portalsRef, contentRef]);

  const handleMouseEnter = useCallback(() => {
    cancelClose();
  }, [cancelClose]);

  const handleMouseLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      ref={contentRef}
      className={classNames(styles['dropdown-content'], styles['dropdown-sub-content'], className)}
      role="menu"
      tabIndex={-1}
      style={{
        ...floatingStyle,
        minWidth: 180,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>,
    document.body,
  );
}

export default DropdownMenu;
